import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
} from "@angular/core";
import { firstValueFrom } from "rxjs";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";

import { VlPlanningComponent } from "../../../../shared/components/vl-planning/vl-planning.component";
import { Widget } from "../../models/dashboard.model";
import { CommandeFacadeService } from "../../../commande/services/store/commande-facade.service";
import { CommandeClientFacadeService } from "../../../commande-client/services/store/commande-client-facade.service";
import { OfFacadeService } from "../../../production/services/of/of-facade.service";
import { CreneauLigneService } from "../../../of-plannif/services/creneau-ligne.service";
import { commandesToPlanningEvents } from "../../../planning/utils/commande-to-planning";
import { commandesClientToPlanningEvents } from "../../../planning/utils/commande-client-to-planning";
import { ofsToPlanningEvents } from "../../../planning/utils/of-to-planning";
import { dechargementsToPlanningEvents } from "../../../planning/utils/dechargement-to-planning";
import {
  expeditionsToPlanningEvents,
  chargementsToPlanningEvents,
} from "../../../planning/utils/expedition-to-planning";
import { pdcToPlanningEvents } from "../../../planning/utils/pdc-to-planning";
import { facturesClientToPlanningEvents } from "../../../planning/utils/facture-client-to-planning";
import { PlanningEvent } from "../../../planning/models/planning-event.model";
import { PlanningPrefsService } from "../../../planning/services/planning-prefs.service";
import { PlanningSourceKey } from "../../../planning/utils/source-key";
import { DechargementApiService } from "../../../commande/services/dechargement-store/dechargement-api.service";
import { BlFacadeService } from "../../../bon-livraison/services/bl-store/bl-facade.service";
import { ChargementApiService } from "../../../bon-livraison/services/store/chargement-api.service";
import { FactureFacadeService } from "../../../facture/services/facture/facture-facade.service";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";

@Component({
  selector: "vl-widget-planning",
  standalone: true,
  imports: [CommonModule, VlPlanningComponent, WidgetTitleComponent],
  template: `
    <div class="h100 flex-col gap-4">
      <vl-widget-title
        [text]="widgetTitle()"
        [widget]="widget()"
        [draggable]="true">
        <ng-content class="drag-handler" select=".drag-handler"></ng-content>
      </vl-widget-title>
      <vl-planning
        [events]="allEvents()"
        persistenceKey="dashboard-planning"
        [defaultMode]="'list'"
        [defaultTodayOffset]="2"
        [defaultCellCount]="7"
        [defaultComfortable]="true"
        [availableSources]="availableSources"
        (eventClick)="onEventClick($event)"
        (pdcPrepaOffsetChange)="onPdcPrepaOffsetChange($event)"
        style="flex: 1; min-height: 0;" />
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetPlanningComponent implements OnInit {
  widget = input<Widget>();
  data = input<any>();
  metadata = input<any>();

  private commandeFacade = inject(CommandeFacadeService);
  private commandeClientFacade = inject(CommandeClientFacadeService);
  private ofFacade = inject(OfFacadeService);
  private dechargementApi = inject(DechargementApiService);
  private blFacade = inject(BlFacadeService);
  private chargementApi = inject(ChargementApiService);
  private factureFacade = inject(FactureFacadeService);
  private creneauService = inject(CreneauLigneService);
  private prefsService = inject(PlanningPrefsService);
  private router = inject(Router);
  private dialogs = inject(DialogsService);

  /** Lightweight planning DTOs (bypass heavy store loadAll). */
  private planningDechargements = signal<any[]>([]);
  private planningChargements = signal<any[]>([]);

  /** Offset "Prépa PDC J-N" — lu au init depuis les prefs, modifié via le menu réglages. */
  pdcPrepaOffset = signal<number>(
    this.prefsService.load("dashboard-planning").pdcPrepaOffsetDays ?? 1,
  );

  /** Liste des sources que ce widget alimente (toutes sauf lot-mouvement). */
  readonly availableSources: PlanningSourceKey[] = [
    "commande",
    "commande-client",
    "of",
    "pdc-prepa",
    "pdc-reinte",
    "dechargement",
    "expedition",
    "chargement",
    "facture-client",
  ];

  private cmdEvents = computed(() =>
    commandesToPlanningEvents(this.commandeFacade.getAllEntities()() ?? []),
  );

  private ccEvents = computed(() =>
    commandesClientToPlanningEvents(
      this.commandeClientFacade.getAllEntities()() ?? [],
    ),
  );

  private ofEvents = computed(() =>
    ofsToPlanningEvents(
      this.ofFacade.getAllEntities()() ?? [],
      this.creneauService.creneaux$.value ?? [],
    ),
  );

  private dechEvents = computed(() =>
    dechargementsToPlanningEvents(this.planningDechargements()),
  );

  private blEvents = computed(() =>
    expeditionsToPlanningEvents(this.blFacade.getAllEntities()() ?? []),
  );

  private chargementEvents = computed(() =>
    chargementsToPlanningEvents(this.planningChargements()),
  );

  private pdcEvents = computed(() =>
    pdcToPlanningEvents(
      this.ofFacade.getAllEntities()() ?? [],
      this.creneauService.creneaux$.value ?? [],
      this.pdcPrepaOffset(),
    ),
  );

  private factureClientEvents = computed(() =>
    facturesClientToPlanningEvents(
      this.factureFacade.getAllEntities()() ?? [],
    ),
  );

  allEvents = computed<PlanningEvent[]>(() => [
    ...this.cmdEvents(),
    ...this.ccEvents(),
    ...this.ofEvents(),
    ...this.dechEvents(),
    ...this.blEvents(),
    ...this.chargementEvents(),
    ...this.pdcEvents(),
    ...this.factureClientEvents(),
  ]);

  /** Compteur de factures client en brouillon (EN_REDACTION, hors avoirs).
   *  Affiché dans le titre du widget en rappel soft. Pas de tuile planning
   *  car pas d'action datée. */
  facturesBrouillonCount = computed(() => {
    const all = this.factureFacade.getAllEntities()() ?? [];
    return all.filter((f: any) => !f.isAvoir && f.etat === "EN_REDACTION").length;
  });

  /**
   * Compteur d'events séparés en {futurs, en retard}.
   * - "En retard" = datePrevu < today AND status non terminal AND dateReel null
   * - "À venir" = tous les autres non réalisés (status ∉ {completed, cancelled})
   * - On ignore les events réalisés/annulés (pas actionnables)
   */
  eventCounts = computed(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayMs = todayStart.getTime();

    let aVenir = 0;
    let enRetard = 0;
    for (const e of this.allEvents()) {
      if (e.status === "completed" || e.status === "cancelled") continue;
      if (e.dateReel) continue;
      if (e.datePrevu && new Date(e.datePrevu).getTime() < todayMs) {
        enRetard += 1;
      } else {
        aVenir += 1;
      }
    }
    return { aVenir, enRetard };
  });

  widgetTitle = computed(() => {
    const { aVenir, enRetard } = this.eventCounts();
    const brouillons = this.facturesBrouillonCount();
    let title = `Activité à venir — ${aVenir} évt${aVenir > 1 ? "s" : ""}`;
    if (enRetard > 0) {
      title += ` · ${enRetard} en retard`;
    }
    if (brouillons > 0) {
      title += ` · ${brouillons} fact. en brouillon`;
    }
    return title;
  });

  ngOnInit(): void {
    this.commandeFacade.loadAllFilter({
      etats: [] as any,
      search: "",
      sort: "createDateTime",
    });
    this.commandeClientFacade.loadAllFilter({
      etats: [] as any,
      search: "",
      sort: "createDateTime",
    });
    this.ofFacade.loadAllFilter({
      dluoDates: { start: null as any, end: null as any },
      ofDates: { start: null as any, end: null as any },
      etats: [] as any,
      lignes: [],
      search: "",
      sort: "createDateTime",
      articleId: "",
    });
    firstValueFrom(this.dechargementApi.loadForPlanning()).then(
      (data) => this.planningDechargements.set(data ?? []),
    );
    this.blFacade.loadAllNotBilled();
    firstValueFrom(this.chargementApi.loadPlanning()).then(
      (data) => this.planningChargements.set(data ?? []),
    );
    this.factureFacade.loadAll();
    this.creneauService.loadAllCreneaux();
  }

  onEventClick(event: PlanningEvent): void {
    // Factures : ouvrir le détail directement en dialog modal
    // (pas de navigation vers la liste).
    if (event.source === "facture-client") {
      const id = event.id.replace(/^facture-client:/, "");
      if (!id) return;
      import("../../../facture/containers/facture-detail-dialog/facture-detail-dialog.component").then(
        (m) => this.dialogs.open(m.FactureDetailDialogComponent, "fit-content", "fit-content", { factureId: id }),
      );
      return;
    }
    if (!event.reference.route) return;
    this.router.navigate(event.reference.route, {
      queryParams: event.reference.queryParams,
    });
  }

  /** Synchronise le signal local quand l'utilisateur change l'offset PDC dans le menu. */
  onPdcPrepaOffsetChange(offset: number): void {
    this.pdcPrepaOffset.set(offset);
  }
}
